/**
 * Share a game's results, every game type: publish (or unpublish) the public
 * results page at games.swtorbit.com/YYYY-MM-DD/CODE, copy or open its link,
 * make the snapshot again, post to Discord, and see where they were posted.
 * Before the game ends, only the "publish when the game ends" switch.
 */

import { useState } from 'react';
import { Alert, Badge, Switch } from '@swtorbit/ui/react';
import { isTerminal, type Game, type GameType, type ResultsDiscordPost } from '@swtorbit/shared-socket-events';
import { useGameResults } from '../../hooks/useGameResults';
import { DiscordPostModal } from './DiscordPostModal';

const KIND_LABEL: Record<ResultsDiscordPost['kind'], string> = {
  text: 'Text channel',
  announcement: 'Announcement channel',
  thread: 'Thread',
  forum_post: 'Forum post',
};

function when(ms: number): string {
  return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function ResultsSharePanel({ game }: { game: Game }) {
  const results = useGameResults(game);
  const { status, loading, error } = results;
  const [posting, setPosting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);
  const ended = isTerminal(game.gameType as GameType, game.status);

  if (status && !status.supported) {
    return <p className="fs-sm text-slate m-0">Sharing results isn&apos;t available for this game type yet.</p>;
  }

  const copy = async () => {
    if (!status?.publicUrl) return;
    try {
      await navigator.clipboard.writeText(status.publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const published = status?.publishedAtUtcMs != null;
  return (
    <section className="card">
      <div className="card__body d-flex flex-column gap-sm">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-sm">
          <h3 className="text-white m-0">🌐 Share results</h3>
          {status?.available && (
            <Badge hue={published ? 'green' : 'slate'}>{published ? 'Published' : 'Not published'}</Badge>
          )}
        </div>

        {!ended && (
          <>
            <p className="fs-sm text-slate m-0">
              Once the game ends, its results can be published as a public page and posted to Discord.
            </p>
            <Switch
              label="Publish the results page as soon as the game ends"
              hint="Players then see the link and QR code in the game-over cinematic."
              checked={status?.autoPublish ?? game.resultsAutoPublish === true}
              disabled={loading}
              onChange={(e) => void results.setAutoPublish(e.target.checked)}
            />
          </>
        )}

        {ended && !status && loading && <p className="fs-sm text-slate m-0">Making the results…</p>}

        {ended && status?.available && (
          <>
            {status.publicUrl && (
              <div className="d-flex flex-wrap align-items-center gap-xs">
                <code className={published ? 'text-cyan' : 'text-slate'}>{status.publicUrl}</code>
                <button className="btn btn--outline btn--slate btn--xs" onClick={copy}>
                  {copied ? 'Copied' : 'Copy'}
                </button>
                {published && (
                  <a className="btn btn--outline btn--cyan btn--xs" href={status.publicUrl} target="_blank" rel="noreferrer">
                    Open
                  </a>
                )}
              </div>
            )}
            <p className="fs-sm text-slate m-0">
              {published
                ? `Anyone with the link can see these results (published ${when(status.publishedAtUtcMs!)}${status.publishedBy ? ` by ${status.publishedBy}` : ''}).`
                : 'Only you can see these results until you publish them; the link shows nothing until then.'}
            </p>

            <div className="d-flex flex-wrap gap-xs">
              {!published && (
                <button className="btn btn--green btn--sm" disabled={loading} onClick={() => void results.publish()}>
                  Publish results page
                </button>
              )}
              {published && !confirmUnpublish && (
                <button className="btn btn--outline btn--red btn--sm" disabled={loading} onClick={() => setConfirmUnpublish(true)}>
                  Unpublish
                </button>
              )}
              {published && confirmUnpublish && (
                <>
                  <button
                    className="btn btn--red btn--sm"
                    disabled={loading}
                    onClick={() => {
                      setConfirmUnpublish(false);
                      void results.unpublish();
                    }}
                  >
                    Yes, take the page down
                  </button>
                  <button className="btn btn--outline btn--slate btn--sm" onClick={() => setConfirmUnpublish(false)}>
                    Keep it
                  </button>
                </>
              )}
              <button className="btn btn--discord btn--sm" disabled={loading} onClick={() => setPosting(true)}>
                Post to Discord…
              </button>
              <button
                className="btn btn--outline btn--slate btn--sm"
                disabled={loading}
                title="Make the results again from the saved game"
                onClick={() => void results.rebuild()}
              >
                Rebuild
              </button>
            </div>

            {status.posts.length > 0 && (
              <div className="d-flex flex-column gap-2xs">
                <span className="fs-xs text-slate text-uppercase fw-bold">Posted to Discord</span>
                {status.posts.map((post) => (
                  <div key={`${post.guildId}:${post.channelId}`} className="d-flex flex-wrap align-items-center gap-xs fs-sm">
                    <span className="text-white">{KIND_LABEL[post.kind]}</span>
                    <a href={post.messageUrl} target="_blank" rel="noreferrer">
                      Open in Discord
                    </a>
                    <span className="text-slate">
                      updated {when(post.updatedAtUtcMs)}
                      {post.postedBy ? ` by ${post.postedBy}` : ' automatically'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {status.snapshotGeneratedAtUtcMs && (
              <span className="fs-xs text-slate">Results made {when(status.snapshotGeneratedAtUtcMs)}</span>
            )}
          </>
        )}

        {error && <Alert hue="red">{error}</Alert>}
      </div>
      {posting && <DiscordPostModal isOpen={posting} onClose={() => setPosting(false)} game={game} results={results} />}
    </section>
  );
}
